import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { UniAdminAuthProvider, useUniAdminAuth } from "../context/UniAdminAuthContext";

import UniAdminLogin from "../pages/university-manager/UMLogin";
import UniAdminForgotPassword from "../pages/university-manager/UMForgotPassword";
import UniAdminResetPassword from "../pages/university-manager/UMResetPassword";
import UniAdminDashboard from "../pages/university-manager/UMDashboard";
import NotFound from "../pages/NotFound";
import NetworkError from "../pages/NetworkError";
import UniAdminCampuses from "../pages/university-manager/UMCampuses";
import UniAdminFaculties from "../pages/university-manager/UMFaculties";
import UniAdminDepartments from "../pages/university-manager/UMDepartments";
import UniAdminClubs from "../pages/university-manager/UMClubs";
import UniAdminEvents from "../pages/university-manager/UMAllEvents";
import UniAdminEventsAwaiting from "../pages/university-manager/UMAwaitingApproval";
import UniAdminUsers from "../pages/university-manager/UMUsers";
import UniAdminUniversityInfo from "../pages/university-manager/UMUniversityInfo";
import UniAdminAuditLog from "../pages/university-manager/UMLogRecords";
import UniAdminAuthority from "../pages/university-manager/UMAuthorityManagement";
import UniAdminFinance from "../pages/university-manager/UMFinanceAnalytics";
import UMAnalytics from "../pages/university-manager/UMAnalytics";

const UniAdminLoginGuard = () => {
  const { isAuthenticated, loading } = useUniAdminAuth();
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/uni-admin/dashboard" replace />;
  return <UniAdminLogin />;
};

const UniAdminProtectedLayout = () => {
  const { isAuthenticated, loading, permissionsLoading } = useUniAdminAuth();
  const location = useLocation();

  if (loading || (isAuthenticated && permissionsLoading)) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/uni-admin/login" state={{ from: location }} replace />;
  }

  return (
    <Routes>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<UniAdminDashboard />} />
      <Route path="university-info" element={<UniAdminUniversityInfo />} />
      <Route path="campuses" element={<UniAdminCampuses />} />
      <Route path="faculties" element={<UniAdminFaculties />} />
      <Route path="departments" element={<UniAdminDepartments />} />
      <Route path="clubs" element={<UniAdminClubs />} />
      <Route path="events" element={<Navigate to="/uni-admin/events/all" replace />} />
      <Route path="events/all" element={<UniAdminEvents />} />
      <Route path="events/awaiting" element={<UniAdminEventsAwaiting />} />
      <Route path="users" element={<UniAdminUsers />} />
      <Route path="audit-log" element={<UniAdminAuditLog />} />
      <Route path="authority-management" element={<UniAdminAuthority />} />
      <Route path="finance" element={<UniAdminFinance />} />
      <Route path="analytics" element={<UMAnalytics />} />
      <Route path="*" element={<NotFound homePath="/uni-admin/dashboard" />} />
    </Routes>
  );
};

const UniAdminRoutes = () => (
  <UniAdminAuthProvider>
    <Routes>
      <Route path="login" element={<UniAdminLoginGuard />} />
      <Route path="forgot-password" element={<UniAdminForgotPassword />} />
      <Route path="reset-password" element={<UniAdminResetPassword />} />
      <Route path="network-error" element={<NetworkError homePath="/uni-admin/dashboard" />} />
      <Route path="*" element={<UniAdminProtectedLayout />} />
    </Routes>
  </UniAdminAuthProvider>
);

export default UniAdminRoutes;
